import { type FC, Fragment, useMemo } from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { Asset, FileMetadataEntry } from '@/types/asset';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const FileInformationAccordion: FC<{
  asset: Asset;
  disabled: boolean;
  disabledMessage?: string;
}> = ({ asset, disabled, disabledMessage }) => {
  const tFileInformation = useTranslations('inspector.fileInformation');
  const fileMetadata = asset?.fileMetadata ?? {};
  const accordionDisabled = disabled || Object.entries(fileMetadata).length === 0;

  const grouped = useMemo<Record<string, Array<FileMetadataEntry>>>(() => {
    return Object.entries(fileMetadata).reduce(
      (acc, [fullKey, { value, description }]) => {
        // split “Exif.Thumbnail.Compression” into ['Exif','Thumbnail','Compression']
        const [root, group] = fullKey.split('.');

        // build a group title, e.g. “Exif Thumbnail”
        const title = `${root} ${group}`;
        if (!acc[title]) {
          acc[title] = [];
        }
        acc[title].push({ description, value });
        return acc;
      },
      {} as Record<string, Array<FileMetadataEntry>>,
    );
  }, [asset?.fileMetadata]);

  return (
    <AccordionItem value="file-information" disabled={accordionDisabled}>
      <AccordionTrigger disabled={accordionDisabled} disabledMessage={disabledMessage}>
        {tFileInformation('title')}
      </AccordionTrigger>
      {!accordionDisabled && (
        <AccordionContent className="flex flex-col p-3">
          <div className="divide-y divide-ui-bevel">
            {asset &&
              Object.entries(grouped).map(([groupTitle, items]) => (
                <Fragment key={groupTitle}>
                  <p className="py-3 font-bold text-text-gray-alternate">{groupTitle}</p>
                  {items.map(({ description, value }, i) => {
                    const displayValue =
                      value === null
                        ? ''
                        : Array.isArray(value)
                          ? value.join(', ')
                          : value instanceof Date
                            ? value.toLocaleString()
                            : String(value);

                    return (
                      <div className="flex flex-row justify-between gap-x-3 py-[6px] text-xs" key={i}>
                        <div className="font-bold">{description}</div>
                        <div
                          title={displayValue.length > 100 ? displayValue : undefined}
                          className={cn('text-right text-[13px]!', displayValue?.length > 100 && 'line-clamp-3')}
                        >
                          {displayValue}
                        </div>
                      </div>
                    );
                  })}
                </Fragment>
              ))}
          </div>
        </AccordionContent>
      )}
    </AccordionItem>
  );
};
